import * as RadioGroup from "@radix-ui/react-radio-group";
import classNames from "classnames";
import React, { CSSProperties, ReactNode, useRef, useState } from "react";
import useSlidingIndicator from "../../../shared/hooks/useSlidingIndicator";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaText from "../typography/Text/Text";
import "./kuika-radio-group.scss";

/** An option's label: a word, or a block that explains the choice under its own heading. */
export interface KuikaRadioGroupOption {
  label: React.ReactNode;
  value: string | number;
}

export type KuikaRadioGroupVariant = "default" | "card";
export type KuikaRadioBadgeTone = "neutral" | "info" | "success" | "warning";

/** A `KuikaRadioGroupOption` that the card variant can also describe, badge, illustrate and block. */
export interface IKuikaRadioOption extends KuikaRadioGroupOption {
  /** card variant only — one line on what picking this option means */
  description?: string;
  /** card variant only — maturity or state signal, e.g. "Beta" */
  badge?: string;
  badgeTone?: KuikaRadioBadgeTone;
  /** card variant only — leading glyph, e.g. a KIcon or KuikaSvg */
  icon?: ReactNode;
  /**
   * card variant only — this option alone cannot be picked (the group-level `disabled` still wins).
   * The card stays visible so a value the form already holds is never silently hidden.
   */
  disabled?: boolean;
  /** card variant only — why the option is blocked; shown as a tooltip on the whole card. */
  disabledReason?: string;
}

export interface IKuikaRadioGroupProps {
  options: IKuikaRadioOption[];
  value?: string | number;
  onChange?: (value: string | number) => void;
  disabled?: boolean;
  defaultValue?: string | number;
  align?: "center" | "top";
  labelPosition?: "right" | "left";
  className?: string;
  variant?: KuikaRadioGroupVariant;
  /** card variant only — grid column count */
  columns?: number;
}

// React 17 has no useId; a module counter hands each mounted group its own id prefix.
let nextRadioGroupId: number = 0;

const KuikaRadioGroup = ({
  options,
  value: controlledValue,
  onChange,
  disabled,
  defaultValue,
  align = "center",
  labelPosition = "right",
  className,
  variant = "default",
  columns = 2
}: IKuikaRadioGroupProps) => {
  const isControlled: boolean = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || options[0]?.value);
  // The label sits beside the radio, not inside it, so each radio names itself after its label
  // through aria-labelledby — without it a screen reader announces a bare "radio button".
  const [groupId] = useState<string>((): string => `kuika-radio-group-${++nextRadioGroupId}`);
  const labelIdOf = (index: number): string => `${groupId}-label-${index}`;
  const value = isControlled ? controlledValue : internalValue;

  // One dot for the whole group, sliding from ring to ring as the pick changes. The rings carry
  // `data-indicator-key`; Radix's own Indicator is kept (drawn invisible) rather than removed.
  const rootRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  useSlidingIndicator(rootRef, value === undefined ? null : value.toString());

  // Radix reports the checked value as a string, while a click on the label hands back whatever
  // the caller put in `options`. Resolve to the caller's own value either way, or a numeric
  // option answers `0` from one path and `"0"` from the other.
  const handleValueChange = (newValue: string | number) => {
    const picked = options.find((option) => option.value.toString() === newValue.toString());
    const resolved = picked ? picked.value : newValue;
    if (!isControlled) {
      setInternalValue(resolved);
    }
    if (onChange) {
      onChange(resolved);
    }
  };

  // A click on the row picks its option — but the radio reports its own clicks through
  // `onValueChange`, so a click that started on it is left to Radix, or one pick is reported twice.
  // A disabled group ignores the row as Radix ignores the radio.
  const handleOptionRowClick = (event: React.MouseEvent<HTMLDivElement>, optionValue: string | number): void => {
    if (disabled) {
      return;
    }
    const target: Element = event.target as Element;
    if (target.closest('[role="radio"]')) {
      return;
    }
    handleValueChange(optionValue);
  };

  // A word goes in the text primitive, which renders a span; a block brings its own markup and
  // would be invalid inside one, so it gets a div of its own.
  const renderLabel = (label: React.ReactNode, side: "left" | "right", id: string): JSX.Element => {
    const spacing: React.CSSProperties = side === "left" ? { marginRight: 8 } : { marginLeft: 8 };
    if (typeof label === "string" || typeof label === "number") {
      return (
        <KuikaText id={id} style={spacing}>
          {label}
        </KuikaText>
      );
    }
    return (
      <div id={id} className="kuika-radio-group__label" style={spacing}>
        {label}
      </div>
    );
  };

  if (variant === "card") {
    const gridStyle: CSSProperties = { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` };

    return (
      <RadioGroup.Root
        className={classNames("kuika-radio-group kuika-radio-group--cards", className)}
        style={gridStyle}
        value={value?.toString()}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        {options.map((option: IKuikaRadioOption) => {
          const isOptionDisabled: boolean = disabled === true || option.disabled === true;
          const card: JSX.Element = (
            <RadioGroup.Item
              key={option.value}
              className="kuika-radio-group__card"
              value={option.value.toString()}
              disabled={isOptionDisabled}
            >
              <span className="kuika-radio-group__card-head">
                {option.icon && <span className="kuika-radio-group__card-glyph">{option.icon}</span>}
                <span className="kuika-radio-group__card-naming">
                  <span className="kuika-radio-group__card-label">{option.label}</span>
                  {option.badge && (
                    <span
                      className={classNames(
                        "kuika-radio-group__card-badge",
                        `kuika-radio-group__card-badge--${option.badgeTone ?? "neutral"}`
                      )}
                    >
                      {option.badge}
                    </span>
                  )}
                </span>
                <span className="kuika-radio-group__card-dot">
                  <RadioGroup.Indicator className="kuika-radio-group__card-dot-fill" />
                </span>
              </span>
              {option.description && <span className="kuika-radio-group__card-description">{option.description}</span>}
            </RadioGroup.Item>
          );
          if (isOptionDisabled && option.disabledReason) {
            return (
              <KuikaTooltip key={option.value} content={option.disabledReason} position="top">
                <span className="kuika-radio-group__card-tooltip-anchor">{card}</span>
              </KuikaTooltip>
            );
          }
          return card;
        })}
      </RadioGroup.Root>
    );
  }

  const wrapperClassName = classNames("kuika-radio-group__item-wrapper", {
    "kuika-radio-group--center": align === "center",
    "kuika-radio-group--top": align === "top",
    "kuika-radio-group--left": labelPosition === "left",
    "kuika-radio-group--right": labelPosition === "right"
  });

  return (
    <RadioGroup.Root
      ref={rootRef}
      className={classNames("kuika-radio-group", className)}
      value={value?.toString()}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      {options.map((option, index: number) => (
        <div
          key={option.value}
          className={wrapperClassName}
          onClick={(event: React.MouseEvent<HTMLDivElement>): void => handleOptionRowClick(event, option.value)}
        >
          {labelPosition === "left" && renderLabel(option.label, "left", labelIdOf(index))}
          <RadioGroup.Item
            className={classNames("kuika-radio-group__item", {
              "kuika-radio-group--selected": value === option.value && !disabled
            })}
            value={option.value.toString()}
            disabled={disabled}
            aria-labelledby={labelIdOf(index)}
            data-indicator-key={option.value.toString()}
          >
            <RadioGroup.Indicator className="kuika-radio-group__indicator"></RadioGroup.Indicator>
          </RadioGroup.Item>
          {labelPosition === "right" && renderLabel(option.label, "right", labelIdOf(index))}
        </div>
      ))}
      <span className="kuika-sliding-indicator kuika-radio-group__dot" aria-hidden="true" />
    </RadioGroup.Root>
  );
};

export default KuikaRadioGroup;
